import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PackageSearch, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, peso, formatApiError } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";
import OrderTimeline from "../components/OrderTimeline";

const STATUS_COLORS = {
  pending: "bg-[#F39C12] text-white",
  paid: "bg-[#2F5233] text-white",
  preparing: "bg-[#3498DB] text-white",
  assigned: "bg-[#8E44AD] text-white",
  out_for_delivery: "bg-[#E67E22] text-white",
  delivered: "bg-[#2F5233] text-white",
  cancelled: "bg-[#E74C3C] text-white",
};

export default function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reorderingId, setReorderingId] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const { add } = useCart();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/orders/mine").then((r) => setOrders(r.data)).finally(() => setLoading(false));
    api.get("/loyalty/me").then((r) => setLoyalty(r.data)).catch(() => { /* noop */ });
  }, []);

  const reorder = async (order) => {
    setReorderingId(order.id);
    let added = 0;
    let skipped = 0;
    for (const item of order.items) {
      try {
        const { data: product } = await api.get(`/products/${item.product_id}`);
        if (!product.active) { skipped++; continue; }
        // Resolve variant if the original order had one
        let variant = null;
        if (item.variant_id && product.variants?.length) {
          variant = product.variants.find((v) => v.id === item.variant_id) || null;
          if (!variant) { skipped++; continue; }
        } else if (product.stock <= 0) {
          skipped++;
          continue;
        }
        const qty = variant ? item.quantity : Math.min(item.quantity, product.stock);
        add(product, qty, variant);
        added++;
      } catch {
        skipped++;
      }
    }
    setReorderingId(null);
    if (added === 0) {
      toast.error("None of these items are available right now");
      return;
    }
    if (skipped > 0) toast.info(`${added} item(s) added, ${skipped} unavailable`);
    else toast.success(`${added} item(s) added to your cart`);
    nav("/cart");
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 page-enter">
      <div className="overline">Your account</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1C2826] tracking-tighter">My Orders</h1>

      {/* Suki Loyalty card */}
      {loyalty && (
        <div className="mt-5 bg-gradient-to-br from-[#2F5233] via-[#1F3D24] to-[#2F5233] text-white rounded-2xl p-5 flex items-center gap-4 shadow-lg overflow-hidden relative" data-testid="suki-points-card">
          <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
            backgroundImage: "radial-gradient(circle at 20% 50%, #F3C97A 1px, transparent 1px), radial-gradient(circle at 70% 30%, #F3C97A 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }} />
          <div className="relative h-12 w-12 rounded-full bg-[#F3C97A]/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-6 w-6 text-[#F3C97A]" />
          </div>
          <div className="relative flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#F3C97A]">Suki Loyalty</div>
            <div className="font-heading font-extrabold text-2xl text-white leading-tight">
              <span data-testid="suki-points-balance">{loyalty.balance}</span> <span className="text-base font-semibold opacity-90">points</span>
            </div>
            <div className="text-[11px] text-white/75 leading-tight mt-0.5">
              {loyalty.earn_rate} · {loyalty.redeem_rate}
            </div>
          </div>
        </div>
      )}

      {loading ? <div className="py-20 flex justify-center"><div className="h-10 w-10 border-4 border-[#2F5233] border-t-transparent rounded-full animate-spin" /></div> :
      orders.length === 0 ? (
        <div className="mt-10 bg-white border border-[#E1E5DF] rounded-2xl p-10 text-center">
          <PackageSearch className="h-12 w-12 text-[#2F5233] mx-auto" strokeWidth={1.5} />
          <div className="mt-3 font-heading font-semibold text-[#1C2826]">No orders yet</div>
          <Link to="/shop" className="text-sm text-[#2F5233] hover:underline">Browse products</Link>
        </div>
      ) : (
        <div className="mt-8 space-y-3" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.id} className="bg-white border border-[#E1E5DF] rounded-2xl p-4 sm:p-5" data-testid={`order-${o.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs text-[#738380]">Order #</div>
                  <div className="font-heading font-bold text-[#1C2826]">{o.number}</div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <Badge className={STATUS_COLORS[o.status] || "bg-gray-200"}>{o.status.replace(/_/g, " ")}</Badge>
                  <div className="font-heading font-extrabold text-[#2F5233] text-base sm:text-lg">{peso(o.total)}</div>
                </div>
              </div>
              <div className="mt-3 text-sm text-[#4A5D5A]">
                {o.items.length} item(s) · {new Date(o.created_at).toLocaleString("en-PH")}
              </div>
              {o.driver_name && (
                <div className="mt-2 text-sm text-[#2F5233]">Driver: <span className="font-semibold">{o.driver_name}</span></div>
              )}

              {/* Visual order tracking timeline */}
              {o.order_type !== "pos" && (
                <div className="mt-4 px-1">
                  <OrderTimeline status={o.status} />
                </div>
              )}

              {/* Loyalty earned / redeemed line */}
              {(o.loyalty_earned > 0 || o.loyalty_redeemed > 0) && (
                <div className="mt-3 text-xs text-[#2F5233] flex flex-wrap gap-x-3 gap-y-1" data-testid={`order-loyalty-${o.id}`}>
                  {o.loyalty_earned > 0 && <span className="font-semibold">+{o.loyalty_earned} Suki points earned</span>}
                  {o.loyalty_redeemed > 0 && <span className="font-semibold">-{o.loyalty_redeemed} points redeemed (₱{o.loyalty_discount} off)</span>}
                </div>
              )}

              {/* Delivery code — show to customer only for active, non-completed orders */}
              {o.delivery_code && !["delivered", "cancelled"].includes(o.status) && (
                <div className="mt-3 bg-[#2F5233] text-white rounded-xl px-4 py-3 flex items-center justify-between gap-3" data-testid={`delivery-code-${o.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#F3C97A]">Your delivery code</div>
                    <div className="text-xs text-white/80 mt-0.5 leading-snug">
                      Give this code to the driver at your door to confirm delivery.
                    </div>
                  </div>
                  <div className="font-mono font-extrabold text-3xl tracking-[0.3em] text-[#F3C97A]" data-testid={`delivery-code-value-${o.id}`}>
                    {o.delivery_code}
                  </div>
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {o.items.slice(0, 5).map((it, idx) => (
                  <div key={`${it.product_id || it.name}-${it.variant_id || ""}-${idx}`} className="flex items-center gap-2 bg-[#F9F9F7] border border-[#E1E5DF] rounded-full px-3 py-1 text-xs">
                    <img src={it.image_url} alt="" className="h-5 w-5 rounded-full object-cover" />
                    {it.quantity}× {it.name}{it.variant_label ? ` · ${it.variant_label}` : ""}
                  </div>
                ))}
                {o.items.length > 5 && <span className="text-xs text-[#738380]">+{o.items.length - 5} more</span>}
              </div>
              <div className="mt-4 flex gap-2">
                <Button
                  size="sm"
                  disabled={reorderingId === o.id}
                  onClick={() => reorder(o)}
                  className="bg-[#E67E22] hover:bg-[#D35400] text-white rounded-full px-4"
                  data-testid={`reorder-${o.id}`}
                >
                  <RefreshCw className={`h-4 w-4 mr-1.5 ${reorderingId === o.id ? "animate-spin" : ""}`} />
                  {reorderingId === o.id ? "Adding..." : "Order again"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
